import { useCallback, useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { useSession } from '@/lib/session';
import Icon from '@/components/Icon';

// ============================================================
// Stocora — Settings › Vendors (vendor admin, 2026-07-07)
// ============================================================
// Edit surface for per-vendor email addresses. Unblocks Drew's
// pilot testing: until now there was no in-app way to set a
// vendor's contact/rep email — vendors were seed-only.
//
// Two addresses per vendor, deliberately distinct:
//   - contact_email : where POs / order emails are SENT, and the
//                     inbound trust anchor (ingest-vendor-email).
//                     "Order email."
//   - rep_email     : sales-rep address for buyback / return /
//                     exchange outreach — the Audit "Email rep"
//                     action. Added 2026-07-07. "Rep email."
//
// Scope (locked with Nick 2026-07-07):
//   - EDIT-ONLY on existing vendors. No create/delete this pass.
//   - rep_email empty => Audit "Email rep" button is disabled
//     with a prompt pointing here; this page is where you fix it.
//
// Permissions: writes are enforced by the vendors_write RLS
// policy (owner/manager). The page mirrors that gate client-side
// off the effective role (viewAsRole ?? real role) so staff see
// a read-only view rather than hitting a silent RLS denial on
// save. The gate is role-direct (not a has_permission catalog
// entry) because vendor writes are enforced by RLS, not by
// has_permission() — adding a catalog entry nothing consults
// would be misleading.
//
// Data pattern mirrors AuditPage: load inside an effect with a
// cancelled flag; a refetchToken bump re-runs it after a save.
// ============================================================

interface VendorRow {
  readonly id: string;
  readonly name: string;
  readonly contact_email: string | null;
  readonly rep_email: string | null;
  readonly rep_name: string | null;
  readonly account_number: string | null;
}

type FetchState =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | { kind: 'loaded'; vendors: readonly VendorRow[] };

// Minimal, forgiving email check — presence of one @ with text
// either side and no whitespace. Intentionally not RFC-strict:
// the goal is to catch obvious typos ("orders@", "no email"),
// not to reject unusual-but-valid addresses. An empty string is
// treated as "clear this field," which is allowed.
function isPlausibleEmail(value: string): boolean {
  const v = value.trim();
  if (v === '') return true; // empty = clear the field
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
}

export default function VendorsSettingsPage() {
  const session = useSession();
  const clinicId = session.activeMembership.clinic_id;
  const effectiveRole = session.viewAsRole ?? session.activeMembership.role;
  const canEdit = effectiveRole === 'owner' || effectiveRole === 'manager';

  const [state, setState] = useState<FetchState>({ kind: 'loading' });
  const [refetchToken, setRefetchToken] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setState({ kind: 'loading' });

    (async () => {
      const { data, error } = await supabase
        .from('vendors')
        .select('id, name, contact_email, rep_email, rep_name, account_number')
        .eq('clinic_id', clinicId)
        .order('name', { ascending: true });

      if (cancelled) return;

      if (error) {
        console.error('[stocora] Vendors fetch failed:', error);
        setState({ kind: 'error', message: error.message });
        return;
      }

      setState({
        kind: 'loaded',
        vendors: (data ?? []) as VendorRow[],
      });
    })();

    return () => {
      cancelled = true;
    };
  }, [clinicId, refetchToken]);

  const handleSaved = useCallback(() => {
    setRefetchToken((n) => n + 1);
  }, []);

  return (
    <div className="p-4 space-y-3">
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => window.history.back()}
          className="text-slate-400 hover:text-slate-700 transition"
          aria-label="Back to settings"
        >
          <Icon name="chevright" className="w-5 h-5 rotate-180" strokeWidth={2} />
        </button>
        <h1 className="text-base font-semibold text-slate-900">Vendors</h1>
      </div>

      <p className="text-[12px] text-slate-500 leading-snug">
        Set where each vendor&apos;s orders are sent, and the sales rep to
        contact for buybacks and returns.
        {!canEdit && (
          <span className="block mt-1 text-slate-400">
            You can view these, but only managers and owners can edit them.
          </span>
        )}
      </p>

      {state.kind === 'loading' && (
        <div className="py-10 flex justify-center" aria-label="Loading vendors">
          <div className="w-6 h-6 border-[3px] border-slate-200 border-t-action rounded-full animate-spin" />
        </div>
      )}

      {state.kind === 'error' && (
        <div className="rounded-lg bg-rose-50 border border-rose-100 p-3 text-[13px] text-rose-800">
          Couldn&apos;t load vendors: {state.message}
        </div>
      )}

      {state.kind === 'loaded' && state.vendors.length === 0 && (
        <div className="py-10 text-center text-slate-500">
          <Icon
            name="mail"
            className="w-9 h-9 mx-auto mb-2 text-slate-300"
            strokeWidth={1.5}
          />
          <p className="text-sm font-medium text-slate-700">No vendors yet</p>
          <p className="text-xs mt-1">
            Vendors appear here once they&apos;re added to your clinic.
          </p>
        </div>
      )}

      {state.kind === 'loaded' &&
        state.vendors.map((vendor) => (
          <VendorCard
            key={vendor.id}
            vendor={vendor}
            clinicId={clinicId}
            canEdit={canEdit}
            onSaved={handleSaved}
          />
        ))}
    </div>
  );
}

// ---- Per-vendor editable card -------------------------------

type SaveState =
  | { kind: 'idle' }
  | { kind: 'saving' }
  | { kind: 'saved' }
  | { kind: 'error'; message: string };

function VendorCard({
  vendor,
  clinicId,
  canEdit,
  onSaved,
}: {
  vendor: VendorRow;
  clinicId: string;
  canEdit: boolean;
  onSaved: () => void;
}) {
  const [contactEmail, setContactEmail] = useState(vendor.contact_email ?? '');
  const [repEmail, setRepEmail] = useState(vendor.rep_email ?? '');
  const [repName, setRepName] = useState(vendor.rep_name ?? '');
  const [account, setAccount] = useState(vendor.account_number ?? '');
  const [save, setSave] = useState<SaveState>({ kind: 'idle' });

  const dirty =
    contactEmail.trim() !== (vendor.contact_email ?? '').trim() ||
    repEmail.trim() !== (vendor.rep_email ?? '').trim() ||
    repName.trim() !== (vendor.rep_name ?? '').trim() ||
    account.trim() !== (vendor.account_number ?? '').trim();

  const contactValid = isPlausibleEmail(contactEmail);
  const repValid = isPlausibleEmail(repEmail);
  const canSave = canEdit && dirty && contactValid && repValid && save.kind !== 'saving';

  const handleSave = useCallback(async () => {
    setSave({ kind: 'saving' });

    // Empty string normalizes to null so the column stays clean
    // (null = "no address on file", which the Audit Email-rep
    // gate checks for).
    const nextContact = contactEmail.trim() === '' ? null : contactEmail.trim();
    const nextRep = repEmail.trim() === '' ? null : repEmail.trim();
    const nextRepName = repName.trim() === '' ? null : repName.trim();
    const nextAccount = account.trim() === '' ? null : account.trim();

    const { error } = await supabase
      .from('vendors')
      .update({
        contact_email: nextContact,
        rep_email: nextRep,
        rep_name: nextRepName,
        account_number: nextAccount,
      })
      .eq('id', vendor.id)
      .eq('clinic_id', clinicId);

    if (error) {
      console.error('[stocora] Vendor save failed:', error);
      const msg =
        error.code === '42501'
          ? "You don't have permission to edit vendors"
          : "Couldn't save — try again";
      setSave({ kind: 'error', message: msg });
      return;
    }

    setSave({ kind: 'saved' });
    onSaved();
  }, [contactEmail, repEmail, repName, account, vendor.id, clinicId, onSaved]);

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-3.5">
      <div className="flex items-center gap-2">
        <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center shrink-0">
          <Icon name="mail" className="w-4 h-4 text-slate-500" strokeWidth={2} />
        </div>
        <div className="text-[14px] font-semibold text-slate-900 truncate">
          {vendor.name}
        </div>
      </div>

      <div className="mt-3 space-y-3">
        <TextField
          label="Account number"
          hint="Your account with this vendor. Shown on POs and buyback emails."
          value={account}
          onChange={setAccount}
          disabled={!canEdit}
          placeholder="44718"
        />
        <EmailField
          label="Order email"
          hint="Where purchase orders are sent."
          value={contactEmail}
          onChange={setContactEmail}
          valid={contactValid}
          disabled={!canEdit}
          placeholder="orders@vendor.com"
        />
        <EmailField
          label="Rep email"
          hint="Sales rep for buybacks, returns, and exchanges."
          value={repEmail}
          onChange={setRepEmail}
          valid={repValid}
          disabled={!canEdit}
          placeholder="rep@vendor.com"
        />
        <TextField
          label="Rep name"
          hint="Used to greet them by name. Falls back to “Hi there,”."
          value={repName}
          onChange={setRepName}
          disabled={!canEdit}
          placeholder="Jane Whitfield"
        />
      </div>

      {canEdit && (
        <div className="mt-3 flex items-center justify-end gap-2">
          {save.kind === 'error' && (
            <span className="text-[11px] text-rose-700 mr-auto">
              {save.message}
            </span>
          )}
          {save.kind === 'saved' && !dirty && (
            <span className="text-[11px] text-success inline-flex items-center gap-1 mr-auto">
              <Icon name="check" className="w-3.5 h-3.5" strokeWidth={2.5} />
              Saved
            </span>
          )}
          <button
            type="button"
            onClick={handleSave}
            disabled={!canSave}
            className="h-9 px-4 rounded-lg bg-action text-white text-[13px] font-semibold hover:bg-action-hover active:bg-action-active transition disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {save.kind === 'saving' ? 'Saving…' : 'Save'}
          </button>
        </div>
      )}
    </div>
  );
}

function EmailField({
  label,
  hint,
  value,
  onChange,
  valid,
  disabled,
  placeholder,
}: {
  label: string;
  hint: string;
  value: string;
  onChange: (v: string) => void;
  valid: boolean;
  disabled: boolean;
  placeholder: string;
}) {
  const showError = !valid && value.trim() !== '';
  return (
    <div>
      <div className="flex items-baseline justify-between">
        <label className="text-[12px] font-medium text-slate-700">{label}</label>
        {showError && (
          <span className="text-[10px] text-rose-600">Check this address</span>
        )}
      </div>
      <input
        type="email"
        inputMode="email"
        autoCapitalize="none"
        autoCorrect="off"
        spellCheck={false}
        value={value}
        disabled={disabled}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className={`mt-1 w-full h-10 px-3 rounded-lg border text-[13px] transition outline-none disabled:bg-slate-50 disabled:text-slate-400 ${
          showError
            ? 'border-rose-300 focus:border-rose-400'
            : 'border-slate-200 focus:border-action'
        }`}
      />
      <p className="mt-1 text-[11px] text-slate-400 leading-snug">{hint}</p>
    </div>
  );
}

/**
 * Plain text field — sibling of EmailField for the non-email vendor
 * attributes (account number, rep name). No validity state: these are
 * free-form strings the vendor gave the clinic, and we have no basis
 * to second-guess their format. Length is bounded by the column CHECK.
 */
function TextField({
  label,
  hint,
  value,
  onChange,
  disabled,
  placeholder,
}: {
  label: string;
  hint: string;
  value: string;
  onChange: (v: string) => void;
  disabled: boolean;
  placeholder: string;
}) {
  return (
    <div>
      <label className="text-[12px] font-medium text-slate-700">{label}</label>
      <input
        type="text"
        value={value}
        disabled={disabled}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full h-10 px-3 rounded-lg border border-slate-200 focus:border-action text-[13px] transition outline-none disabled:bg-slate-50 disabled:text-slate-400"
      />
      <p className="mt-1 text-[11px] text-slate-400 leading-snug">{hint}</p>
    </div>
  );
}
